'use client';

import PaperPlaneIconSvg from '@/assets/svgs/paper-plane.svg';
import {
  ErrorValidateMessageResponse,
  useValidateMessage,
} from '@/containers/Jobs/hooks/useValidateMessage';
import { selectReplyTemplates } from '@/containers/Jobs/redux/jobEditors.selectors';
import {
  addReplyTemplate,
  updateReplyTemplate,
} from '@/containers/Jobs/redux/jobEditors.slice';
import { JobTemplate, JobTemplateMedia } from '@/containers/Jobs/types';
import { MediaItem } from '@/containers/MediaManager/types';
import useTextEditor from '@/containers/TextEditor/hooks/useTextEditor';
import TextEditor from '@/containers/TextEditor/TextEditor';
import { replaceRawAttributes } from '@/containers/TextEditor/utils';
import { useAppDispatch } from '@/lib/redux/store';
import useDebounce from '@/shared/hooks/useDebounce';
import { Editor } from '@tiptap/react';
import { AxiosError } from 'axios';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modals } from '../all-modals';
import ModalBody from '../ModalBody';
import ModalContainer from '../ModalContainer';
import ModalHeader from '../ModalHeader';
import { ModalProps, ReplyTemplateModalParams } from '../types';
import useModals from '../useModals';

const modalName = Modals.ReplyTemplateModal;

interface ReplyTemplateModalProps extends ModalProps {
  className?: string;
  params?: ReplyTemplateModalParams;
}

const ReplyTemplateModal = ({
  className,
  variant = 'narrow',
  params,
}: ReplyTemplateModalProps) => {
  const dispatch = useAppDispatch();
  const { isModalOpen, onClose } = useModals({
    modalName,
  });

  // Extract params
  const jobId = params?.jobId || '';
  const templateId = params?.templateId;
  const mode = params?.mode || 'create';

  // Get existing templates to find the one being edited
  const replyTemplates = useSelector(selectReplyTemplates(jobId));
  const existingTemplate = templateId
    ? replyTemplates.find((t) => t.id === templateId)
    : null;

  // Editor state - isolated from initial message
  const [editor, setEditor] = useState<Editor | null>(null);
  const [title, setTitle] = useState('');

  // Isolated media state for this template
  const [templateMedia, setTemplateMedia] = useState<JobTemplateMedia | null>(null);

  // Create context for useTextEditor with isolated media state
  const textEditorContext = {
    media: templateMedia,
    jobId: `${jobId}-reply-template-${templateId || 'new'}`, // Unique context to avoid conflicts
  };

  const { editorPlainText } = useTextEditor(editor, textEditorContext);

  // Validation state
  const [validationError, setValidationError] = useState<string>('');
  const [isValidating, setIsValidating] = useState(false);

  // Debounce the editor text for validation
  const debouncedEditorText = useDebounce(editorPlainText || '', 1500);

  // Validation hook
  const {
    data: validateMessageData,
    error: validateMessageError,
    isFetching: isValidateMessageLoading,
  } = useValidateMessage(debouncedEditorText);

  // Handle media selection from media manager
  const handleMediaSelect = (media: MediaItem) => {
    // Convert MediaItem to JobTemplateMedia format
    const jobTemplateMedia: JobTemplateMedia = {
      media_id: media.media_id,
      title: media.title,
      media_type: media.media_type as 'IMAGE' | 'VIDEO',
      mime_type: media.mime_type as string,
      preview_url: media.preview_url,
      thumbnail_url: media.thumbnail_url,
    };
    setTemplateMedia(jobTemplateMedia);
  };

  // Initialize editor content for edit mode
  useEffect(() => {
    if (editor && existingTemplate && mode === 'edit') {
      const formattedText = replaceRawAttributes(existingTemplate.text);
      editor.commands.setContent(formattedText, { emitUpdate: true });
    }
  }, [editor, existingTemplate, mode]);

  // Initialize title and media for edit mode
  useEffect(() => {
    if (existingTemplate && mode === 'edit') {
      setTitle(existingTemplate.title);
      setTemplateMedia(existingTemplate.media || null);
    } else {
      // Reset for create mode
      setTitle('');
      setTemplateMedia(null);
    }
  }, [existingTemplate, mode, isModalOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isModalOpen) {
      setTitle('');
      setTemplateMedia(null);
      if (editor) {
        editor.commands.clearContent();
      }
    }
  }, [isModalOpen, editor]);

  // Handle validation results
  useEffect(() => {
    setIsValidating(isValidateMessageLoading);

    if (!isValidateMessageLoading) {
      if (validateMessageError) {
        const errorResponse = (validateMessageError as AxiosError).response
          ?.data as ErrorValidateMessageResponse;
        setValidationError(errorResponse.Error?.message?.[0] ?? 'Validation failed');
      } else {
        setValidationError('');
      }
    }
  }, [validateMessageData, validateMessageError, isValidateMessageLoading]);

  const handleSave = () => {
    if (!title.trim() || !editorPlainText?.trim()) {
      return; // Don't save if title or content is empty
    }

    // Don't save if there are validation errors
    if (validationError) {
      return;
    }

    const templateData: Omit<JobTemplate, 'id'> = {
      title: title.trim(),
      text: editorPlainText,
      is_default: false,
      has_dynamic_media: !!templateMedia,
      has_dynamic_media_rendered: false,
      media: templateMedia || undefined,
    };

    if (mode === 'edit' && templateId) {
      dispatch(
        updateReplyTemplate({
          jobId,
          templateId,
          props: templateData,
        })
      );
    } else {
      dispatch(
        addReplyTemplate({
          jobId,
          template: templateData,
        })
      );
    }

    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const isFormValid =
    title.trim() && editorPlainText?.trim() && !validationError && !isValidating;

  return (
    <div className={classNames('relative', className)}>
      <input
        type="checkbox"
        id={modalName}
        className="modal-toggle"
        checked={isModalOpen}
        onChange={() => {}}
      />

      <ModalContainer
        isModalOpen={isModalOpen}
        modalName={modalName}
        variant={variant}
        onClose={onClose}>
        <div className="flex h-full flex-col">
          {/* Header */}
          <ModalHeader
            modalName={modalName}
            closeAction="state"
            icon={<PaperPlaneIconSvg className="h-5 w-5" />}
            title={mode === 'edit' ? 'Edit Reply Template' : 'Create Reply Template'}
          />

          {/* Body */}
          <ModalBody className="flex-1 px-5 !py-0">
            {/* Spacing for consistency */}
            <div className="h-6" />

            {/* Form */}
            <div className="flex flex-col gap-4 pb-4">
              {/* Title Input */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">Template Title</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered w-full"
                  placeholder="Enter template title..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              {/* Editor */}
              <div className="form-control flex-1">
                <label className="label">
                  <span className="label-text font-medium">Template Content</span>
                </label>
                <div className="min-h-[300px] flex-1">
                  <TextEditor
                    containerClassName="min-h-[300px] p-2 border border-gray-300 rounded-lg"
                    setEditor={setEditor}
                    onChange={() => {}}
                    tools={[
                      'fonts',
                      'b',
                      'i',
                      'attributes',
                      'emoji',
                      'media',
                      'links',
                      'undo',
                      'redo',
                    ]}
                    shouldAppendStopMessage={false}
                    jobId={jobId}
                    placeholderText="Type your reply template content here..."
                    onMediaSelected={handleMediaSelect}
                  />
                </div>
              </div>

              {/* Validation Error Display */}
              {validationError && (
                <div className="form-control">
                  <div className="alert alert-error">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 shrink-0 stroke-current"
                      fill="none"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>{validationError}</span>
                  </div>
                </div>
              )}

              {/* Validation Loading Indicator */}
              {isValidating && (
                <div className="form-control">
                  <div className="dark:border-base-300 dark:bg-base-200 flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <span className="loading loading-spinner loading-sm"></span>
                    <span className="dark:text-base-content text-gray-600">
                      Validating message...
                    </span>
                  </div>
                </div>
              )}

              {/* Media Preview */}
              {templateMedia && (
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">Selected Media</span>
                  </label>
                  <div className="flex items-center gap-3 rounded-lg border border-gray-200 p-3">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={templateMedia.thumbnail_url || templateMedia.preview_url}
                      alt={templateMedia.title}
                      className="h-12 w-12 rounded object-cover"
                    />
                    <div className="flex-1">
                      <div className="font-medium">{templateMedia.title}</div>
                      <div className="text-sm text-gray-500">
                        {templateMedia.media_type.toLowerCase()}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setTemplateMedia(null)}>
                      Remove
                    </button>
                  </div>
                </div>
              )}
            </div>
          </ModalBody>

          {/* Footer */}
          <div className="border-t border-gray-200 px-5 py-4">
            <div className="flex justify-end gap-3">
              <button type="button" className="btn btn-outline" onClick={handleCancel}>
                Cancel
              </button>
              <button
                type="button"
                className={classNames('btn btn-primary', {
                  'btn-disabled': !isFormValid,
                })}
                onClick={handleSave}
                disabled={!isFormValid}>
                {mode === 'edit' ? 'Update Template' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      </ModalContainer>
    </div>
  );
};

export default ReplyTemplateModal;
